<script setup lang="ts">
import MapView from './_components/MapView.vue';
import ContentNavigation from './_components/ContentNavigation.vue';
import HotelNearby from './_components/page-sections/HotelNearby.vue';
import RoomsSection from './_components/page-sections/RoomsSection.vue';
import PageHeader from './_components/page-sections/PageHeader.vue';
import AttractionsSection from './_components/page-sections/AttractionsSection.vue';
import CommentsSection from './_components/page-sections/CommentsSection.vue';
import AmenitiesSection from './_components/page-sections/AmenitiesSection.vue';
import DescriptionSection from './_components/page-sections/DescriptionSection.vue';
import TermsSection from './_components/page-sections/TermsSection.vue';
import SliderSection from './_components/page-sections/SliderSection.vue';
import DetailsSection from './_components/page-sections/DetailsSection.vue';
import { useData } from './_composables/useData';
import FaqSection from './_components/page-sections/FaqSection.vue';
import HotelAlerts from './_components/HotelAlerts.vue';
import SafarmarketAlert from '@components/ui/hotelPage&reserve/SafarmarketAlert.vue';
import FooterButtons from '@components/util/FooterButtons.vue';
import ReserveBill from '@components/ui/hotelPage&reserve&view/reserve-bill/ReserveBill.vue';
import type HotelPriceClass from '@itinerary/hotel/HotelPriceClass';
import type HotelRoomType from '@itinerary/hotel/HotelRoomType';
import { type DateSpanObject, PaxType } from '@utils/types';
import { ReserveType, type VerifyPathError } from '@utils/itinerary';
import { type HotelPathData } from '@utils/hotel';
import useBillEntries from '@composable/useBillEntries';

//#region definitions
const route = useRoute();
const router = useRouter();
const searchFormStore = useSearchFormStore();
const contentStore = useContentStore();
const { searchResultDescription } = classToRefs(contentStore);
const formStore = useFormStore();
const invoiceStore = useInvoiceStore();
const { preInvoiceTotalPrice } = classToRefs(invoiceStore);
const hotelModule = useHotelModule();
const hotelForm = useHotelForm();
const packageSelectStore = usePackageSelectStore();
const nearby = ref<InstanceType<typeof HotelNearby>>();
const bottom = useViewPortBottom();
const gtm = useGtm();
const { preBillDiscounts, preBillEntries, preBillLoading } = useBillEntries();
const { isMobile } = useSize();
const isMounted = useMounted();
const reachedRooms = ref(false);
const showMap = ref(false);
const analyticsStore = useAnalyticsStore();
const authStore = useAuthStore();
const currentSearchForm = useCurrentSearchForm();
const dateModel = computed({
    get() {
        return {
            start: hotelForm.fromDate,
            end: hotelForm.toDate,
        };
    },
    set(value: DateSpanObject) {
        if (value.start) {
            hotelForm.fromDate = value.start;
        }
        hotelForm.toDate = value.end;
    },
});
const searchRooms = () => {
    const urlParams = hotelForm.parsePath(pathData.value);
    if (urlParams && dateModel.value.start && dateModel.value.end) {
        invoiceStore.clearAllEntries();
        hotelModule.changeCurrentSelectedPackageInfo({
            fromDate: dateModel.value.start.toISOString(),
            toDate: dateModel.value.end.toISOString(),
        });
        hotelModule.loadCurrentSelectedPackageRooms();
        searchFormStore.urlParams = {
            ...urlParams,
            fromDate: dayConfig
                .gregory(dateModel.value.start)
                .format(formatter()),
            toDate: dayConfig.gregory(dateModel.value.end).format(formatter()),
        };
        router.replace({
            query: {
                ...route.query,
                fromDate: dayConfig
                    .jalali(dateModel.value.start)
                    .format(formatter()),
                toDate: dayConfig
                    .jalali(dateModel.value.end)
                    .format(formatter()),
            },
        });
    }
};
const resetDate = () => {
    if (hotelModule.isLoadingRooms) {
        return;
    }
    const urlParams = hotelForm.parsePath(pathData.value);
    dateModel.value = {
        start: dayConfig.gregory(urlParams.fromDate).startOf('day').toDate(),
        end: dayConfig.gregory(urlParams.toDate).startOf('day').toDate(),
    };
};
const { initSm } = useSafarMarket();

const initializer = async () => {
    try {
        packageSelectStore.clearSelectedPackages();
        await hotelForm.verifyViewPath(route.path, pathData.value);
        await contentStore.getSearchResultContents({
            reserveType: ReserveType.Hotel,
            destination: pathData.value.city ?? '',
            hotelName: pathData.value.hotelName,
        });
        await hotelModule.initPackageFromPath(
            hotelForm.parsePath(pathData.value)
        );
    } catch (er) {
        const err = er as VerifyPathError;
        if (err.needRedirect && err.status && err.newUrl) {
            await navigateTo(
                {
                    path: err.newUrl,
                    query: { ...route.query, ...err.queries },
                },
                { redirectCode: err.status, replace: err.replace }
            );
        } else if (err.status !== undefined && err.message !== undefined) {
            showError({
                statusCode: err.status,
                message: err.message,
            });
        } else {
            grayLog()?.log(`${er}`, JSON.stringify(er), {
                path: route.fullPath,
            });
            reportException(er);
            showError({ statusCode: 500 });
        }
    }
};
//#endregion

//#region computed
const { hotelPackage, title } = useData();
const tabItems = computed(() =>
    [
        {
            href: '#amenities',
            title: 'امکانات و ویژگی‌ها',
            condition: !!amenities.value.length,
        },
        {
            href: '#rooms',
            title: 'انتخاب اتاق',
        },
        {
            title: 'معرفی هتل',
            href: '#intro',
            condition: descriptionCondition.value,
        },
        {
            href: '#attractions',
            title: 'فاصله تا نقاط مهم',
            condition: !!attractionsCondition.value,
        },
        {
            title: 'نظرات کاربران',
            href: '#comments',
            condition: !!comments.value.length,
        },
        {
            title: 'قوانین و مقررات',
            href: '#rules',
            condition: !!rulesCondition.value,
        },
    ].filter(item => item.condition !== false)
);
const isLoading = computed(() => hotelModule.isLoadingHotelInfo);
const center = computed(() => ({
    lat: hotelPackage.value.latitude!,
    lng: hotelPackage.value.longitude!,
}));
const amenities = computed(() =>
    hotelPackage.value.amenities.filter(am => am.title.length < 30)
);

const hotelClosed = computed(() => hotelPackage.value.closed);
const photo = computed(() => hotelPackage.value.photos[0]?.large ?? '');
const isOneSelectableRoomSelected = computed(
    () =>
        hotelPackage.value.selectedPricesWithPriceClass &&
        hotelPackage.value.selectedPricesWithPriceClass[0] &&
        hotelPackage.value.selectedPricesWithPriceClass[0][1]!
            .onlyThisTypeSelectable
);
const prices = computed(() => {
    if (isOneSelectableRoomSelected.value) {
        return hotelPackage.value.prices.filter(
            p => p.id === hotelPackage.value.selectedPrices![0].id
        );
    } else {
        return hotelPackage.value.prices;
    }
});
const comments = computed(() => {
    if (hotelPackage.value.hotelName === 'jamali-mashhad') {
        return hotelPackage.value.comments.filter(
            c => c.rating && c.rating > 4
        );
    }
    return hotelPackage.value.comments;
});
const loadingRoom = computed(
    () => isLoading.value || hotelModule.isLoadingRooms !== false
);
const selectedPriceClasses = computed(
    () =>
        (hotelPackage.value.selectedPricesWithPriceClass || []) as [
            HotelPriceClass,
            HotelRoomType,
        ][]
);
const reserveButtonText = computed(() =>
    reachedRooms.value || selectedPriceClasses.value.length
        ? 'ادامه فرایند رزرو'
        : 'مشاهده اتاق‌ها'
);
const reserveButtonDisabled = computed(
    () => reachedRooms.value && !isRoomSelected.value
);
const isRoomSelected = computed(() => !!selectedPriceClasses.value.length);
const showInvoiceInAppBar = computed(
    () => reachedRooms.value || isRoomSelected.value
);
const pathData = computed(
    () => ({ ...route.params, ...route.query }) as HotelPathData
);

const showPreBill = computed(
    () =>
        preInvoiceTotalPrice.value ||
        (preBillEntries.value.length &&
            packageSelectStore.currentPackageIsFloating)
);
const isFloating = computed(() => packageSelectStore.currentPackageIsFloating);
const descriptionCondition = computed(() => !!hotelPackage.value.description);
const attractionsCondition = computed(
    () => hotelPackage.value.popularPlaces.length
);
const rulesCondition = computed(
    () =>
        hotelPackage.value.checkoutHour ||
        hotelPackage.value.checkinHour ||
        hotelPackage.value.termsAndConditions
);
//#endregion

//#region methods
const scrollToNearby = () =>
    nearby.value?.$el.scrollIntoView({
        behavior: 'smooth',
    });
const navToRooms = () => navigateToElement('#rooms');
const reserveClick = () => {
    if (!isRoomSelected.value) {
        navToRooms();
        return;
    }
    gtm?.trackEvent({ event: 'add_to_cart' });
    formStore.itineraryPackages = packageSelectStore.selectedPackages;
    hotelForm.setPaxTypeCount({
        type: PaxType.Adult,
        count: selectedPriceClasses.value.length,
    });
    gtm?.trackEvent({
        event: 'click reserve button-hdp',
    });
    if (packageSelectStore.hasRaisePage) {
        router.push('/reserve/raise');
    } else {
        router.push('/reserve/form');
    }
};
const mobileReserveClick = () => {
    if (!showInvoiceInAppBar.value) {
        navToRooms();
    } else {
        reserveClick();
    }
};

const { start, stop } = useReload({
    onTimeout: () => hotelModule.loadCurrentSelectedPackageRooms(),
});
//#endregion

//#region watches
watch(loadingRoom, val => {
    if (!val) {
        const hasReservableRooms = prices.value.some(
            price => price.capacity > 0
        );
        const hasNonReservableRooms = prices.value.some(
            price => price.capacity > 0
        );
        hotelModule.trackLoadRooms(
            isFloating.value
                ? 'floatingReserve'
                : hasNonReservableRooms && hasReservableRooms
                  ? 'haveReservableAndNoneReservableList'
                  : hasReservableRooms
                    ? 'haveReservableList'
                    : hasNonReservableRooms
                      ? 'haveNoneReservableList'
                      : 'noResults'
        );
        const { websiteLink } = route.query;
        if (websiteLink) {
            const selectedPriceClass = prices.value
                .map(price => price.classes)
                .flat()
                .find(cls => cls.uniqueId === websiteLink);
            if (selectedPriceClass) {
                packageSelectStore.selectPriceClassForPackageIndex({
                    priceClass: selectedPriceClass,
                    count: 1,
                    packageIndex: 0,
                });
                invoiceStore.loadPreInvoice(
                    packageSelectStore.selectedPackages[0]
                );
                reserveClick();
            }
        }
    }
});
//#endregion

//#region page setup
onServerPrefetch(async () => {
    await initializer();
});

await useAsyncData(
    async () => {
        if (!hotelModule.ssrHotelPackage) {
            await initializer();
        }
        invoiceStore.clearAllEntries();

        const current = hotelPackage;
        analyticsStore.trackAll('select_hotel', {
            isDiscounted: `${current.value.selectedPrices?.some(
                p => p.isDiscounted
            )}`,
            isFeatured: 'false',
            isSameService: `${
                current.value.reserveType === searchFormStore.firstReserveType
            }`,
            isMasir: 'false',
            city: current.value.cityName,
            cityTitle: current.value.cityTitle,
            hotelName: current.value.title,
            hotelEngName: current.value.hotelName,
            creationSource: authStore.siteSourceId,
            whiteLabel: 'mrbilit',
        });
        initSm(route.fullPath);
        if (
            !packageSelectStore.selectedPackages.length &&
            !hotelModule.ssrHotelPackage
        ) {
            await hotelModule.initPackageFromPath(
                hotelForm.parsePath(pathData.value)
            );
        } else if (hotelModule.ssrHotelPackage) {
            const { fromDate, toDate } = hotelModule.revalidateHotelPackages(
                hotelModule.ssrHotelPackage
            );
            hotelForm.fromDate = fromDate || new Date();
            hotelForm.toDate = toDate || new Date();
        }

        hotelForm.setUserDestinationFromPackage(current.value);
        searchFormStore.initSearchForm(
            hotelForm.generateViewUrl({
                cityName: current.value.cityName,
                fromDate: hotelForm.fromDate?.toISOString(),
                toDate: hotelForm.toDate?.toISOString(),
                adultCount: hotelForm.adultCount.toString(),
                childCount: hotelForm.childCount.toString(),
                roomCount: hotelForm.roomCount.toString(),
            })
        );
        hotelModule.loadNearbyHotels({
            hotelId: hotelPackage.value.id!,
            from: hotelPackage.value.fromDate!,
            to: hotelPackage.value.toDate!,
        });
        if (
            !packageSelectStore.selectedPackages[0]?.prices.length &&
            !hotelClosed.value
        ) {
            hotelModule.loadCurrentSelectedPackageRooms();
        } else {
            invoiceStore.loadPreInvoice(hotelPackage.value);
        }

        formStore.setCounts({
            adultCount: currentSearchForm.value.adultCount,
            childCount: currentSearchForm.value.childCount,
            infantCount: currentSearchForm.value.infantCount,
        });
        return true;
    },
    { server: false, lazy: true }
);
const headTagTitle = computed(
    () =>
        `رزرو آنلاین ${title.value} ${hotelPackage.value.cityTitle} با تخفیف ویژه | مِستربلیط`
);
const headTagDesc = computed(
    () =>
        searchResultDescription.value ||
        `رزرو آنی و با تخفیف هتل ${title.value} با آفرهای ويژه و امکان کنسلی آنلاین و پشتیبانی ۲۴ ساعته واقعی فقط در مِستربلیط`
);
const dateInvoiceFixed = computed(() => isMounted.value && !isMobile.value);
useServerHead({
    link: [
        {
            rel: 'canonical',
            href: `https://mrbilit.com${route.path
                .replace(/(\/hotel\/[a-zA-Z]+\/[a-zA-Z]+)\/(\d.+)/, '$1')
                .replace(/\/$/, '')}`,
        },
    ],
});
useSeoMeta({
    title: headTagTitle,
    description: headTagDesc,
    ogTitle: headTagTitle,
    ogDescription: headTagDesc,
});
definePageMeta({
    scrollToTop: false,
    validate: ({ path }) => path !== '/hotelPage',
});
useSchemaOrg([
    defineAddress({
        '@context': 'https://schema.org',
        '@type': 'Hotel',
        name: title,
        description: computed(() => hotelPackage.value.description),
        address: {
            '@type': 'PostalAddress',
            addressCountry: 'iran',
            streetAddress: computed(() => hotelPackage.value.address),
        },
        telephone: '+982161169000',
        image: photo,
        photo,
        starRating: {
            '@type': 'Rating',
            ratingValue: computed(() => hotelPackage.value.hotelStar),
        },
        priceRange: `${Math.round(Math.random() * 1000) * 10000} - ${
            Math.round(Math.random() * 1000) * 100000
        }`,
    }),
    defineProduct({
        '@context': 'https://schema.org',
        '@type': 'Product',
        aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: computed(() => hotelPackage.value.points10),
            reviewCount: computed(() => comments.value.length),
        },
        description: computed(() => hotelPackage.value.description),
        name: title,
        image: photo,
        photo,
        // hotelPackage.value.payable is not available
        // offers: {
        //     '@type': 'Offer',
        //     availability: `https://mrbilit.com${useRoute().fullPath}`,
        //     price: computed(() => hotelPackage.value.payable),
        //     priceCurrency: 'IRR',
        // },
        review: computed(() =>
            comments.value.length
                ? comments.value.map(item =>
                      defineReview({
                          '@type': 'Review',
                          author: item.customer,
                          datePublished: item.postedOn,
                          reviewBody: item.comment,
                          inLanguage: 'fa-IR',
                          reviewRating: {
                              '@type': 'Rating',
                              bestRating: 10,
                              ratingValue: item.rating,
                              worstRating: 0,
                          },
                      })
                  )
                : undefined
        ),
    }),
]);
//#endregion

//#region lifecycle
onMounted(() => {
    // As we remember, putting this code here makes some undesired behavior
    // but, we don't remember the behavior.
    // As an urgent, we put this here, so if in the future some bugs are reported
    // may be because this line of code.
    invoiceStore.clearState();
    history.replaceState(
        {
            ...history.state,
            redirect: `/hotel/${route.params.city}?${new URLSearchParams(route.query as Record<string, string>).toString()}`,
        },
        ''
    );
    gtm?.trackEvent({ event: 'view_item' });
    start();
});
onUnmounted(() => hotelModule.clear());
onBeforeUnmount(() => stop());
//#endregion
</script>

<template>
    <div class="hotel-page-container">
        <PageHeader />
        <div class="page-root">
            <FooterButtons
                mobile-only
                fixed
                class="fixed-proceed"
                :proceed-button-name="reserveButtonText"
                :proceed-button-disabled="reserveButtonDisabled"
                @click:proceed="mobileReserveClick"
            >
                <template #prepend>
                    <ReserveBill
                        v-if="preBillEntries.length"
                        :items="preBillEntries"
                        :discounts="preBillDiscounts"
                        :loading="preBillLoading"
                        :messages="packageSelectStore.billMessages"
                        hide-credit
                        initial-state="closed"
                    />
                </template>
            </FooterButtons>
            <div class="left-container">
                <SafarmarketAlert />
                <SliderSection class="mobile-slider-section" for-mobile />
                <DetailsSection
                    class="section-container"
                    @show-map="showMap = true"
                />
                <HotelAlerts class="alerts" />
                <mr-fix
                    :offset-top="20"
                    :offset-stop="8"
                    stop-element="#stop"
                    :disabled="!dateInvoiceFixed"
                >
                    <div class="section-container date-invoice">
                        <mr-datepicker
                            v-model="dateModel"
                            hide-description
                            class="date-picker"
                            include-day-name
                            start-day-title="ورود"
                            end-day-title="خروج"
                            format="long"
                            :min-date="new Date()"
                            range="enabled"
                            count-nights
                            unequal-range
                            :modal-z-index="2147483647"
                            @done="searchRooms"
                            @close="resetDate"
                        />
                        <transition>
                            <ReserveBill
                                v-show="showPreBill"
                                class="desktop-only"
                                :items="preBillEntries"
                                :discounts="preBillDiscounts"
                                :messages="packageSelectStore.billMessages"
                                hide-credit
                            />
                        </transition>
                        <mr-button
                            full-width
                            :disabled="reserveButtonDisabled"
                            @click="reserveClick"
                        >
                            {{ reserveButtonText }}
                        </mr-button>
                    </div>
                </mr-fix>
            </div>
            <div class="right-container">
                <SliderSection class="desktop-slider-section" />
                <mr-flash-message
                    v-if="hotelClosed"
                    message="در حال حاضر هتل امکان ارائه خدمات ندارد."
                />
                <HotelNearby v-if="hotelClosed" ref="nearby" />
                <ContentNavigation
                    :tabs="tabItems"
                    :items-min-width="167.6"
                    :offset-width="0"
                    :scroll-offset="-48"
                    max-width="auto"
                    stop-element-id="__footer"
                    class="content-navigation"
                />
                <AmenitiesSection
                    v-if="amenities.length"
                    id="amenities"
                    class="section-container"
                />
                <RoomsSection
                    v-if="!hotelClosed"
                    id="rooms"
                    v-model:into-view="reachedRooms"
                    class="section-container"
                    @research="scrollToNearby"
                />
                <DescriptionSection
                    v-if="descriptionCondition"
                    id="intro"
                    class="section-container"
                />
                <AttractionsSection
                    v-if="attractionsCondition"
                    id="attractions"
                    class="section-container"
                    @show-map="showMap = true"
                />
                <TermsSection
                    v-if="rulesCondition"
                    id="rules"
                    class="section-container"
                />
                <CommentsSection id="comments" class="section-container" />
            </div>
        </div>
        <mr-flash-message
            v-if="hotelPackage.message"
            :message="hotelPackage.message"
            type="warning"
            style="margin: 16px 0"
        />
        <div id="stop" />
        <HotelNearby v-show="!hotelClosed" ref="nearby" />
        <FaqSection />
        <MapView v-if="showMap" :is-loading :center @close="showMap = false" />
    </div>
</template>

<style>
html {
    scroll-behavior: smooth;
}
</style>

<style lang="scss" scoped>
@include v-transition-scale-y();
.hotel-page-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    background: usePalette($p-cool-gray, $p-100);
    padding-bottom: 24px;
    position: relative;
    .page-root {
        display: flex;
        flex-direction: row-reverse;
        width: 100%;
        max-width: 1200px;
        gap: 20px;
        z-index: 10;
        @include mobile-only {
            flex-direction: column;
        }
        .mobile-slider-section {
            @include desktop-only {
                display: none;
            }
        }
        .desktop-slider-section {
            @include mobile-only {
                display: none;
            }
        }
        .right-container {
            flex-grow: 1;
            display: flex;
            flex-basis: calc(min(100%, 1200px) - 370px);
            max-width: calc(min(100%, 1200px) - 370px);
            flex-direction: column;
            gap: 32px;
            @include mobile-only {
                flex-basis: 100%;
                max-width: 100%;
                gap: 12px;
            }
            .content-navigation {
                @include mobile-only {
                    display: none;
                }
            }
            .select-trips-title {
                @include tg-xl($w7);
                margin-bottom: 16px;
            }
            .section-container {
                display: flex;
                flex-direction: column;
                gap: 8px;
                @include mobile-only {
                    margin-inline: 12px;
                }
            }
        }
        .left-container {
            flex-basis: 350px;
            max-width: 350px;
            flex-shrink: 0;
            display: flex;
            flex-direction: column;
            gap: 20px;
            @include mobile-only {
                flex-basis: 100%;
                max-width: 100%;
                gap: 12px;
            }
            .alerts {
                @include mobile-only {
                    padding-inline: 12px;
                }
            }
            .section-container {
                display: flex;
                flex-direction: column;
                gap: 12px;
                background-color: white;
                border-radius: 12px;
                padding: 20px;
                @include shadow;
                @include mobile-only {
                    margin-inline: 12px;
                }
                &.date-invoice {
                    @include v-transition-scale-y;
                }
            }
        }
    }
    .fixed-proceed {
        @include mobile-only {
            display: flex;
            position: fixed;
            bottom: v-bind(bottom);
            right: 0;
            z-index: 100000000;
        }
    }
}
</style>
